/**
 * Habit tracking: the saved shape, streaks, and the per-month numbers.
 *
 * A tick is stored as an ISO date (`2026-10-05`) in a list per habit. Streaks are counted
 * on day numbers (whole days since 1970-01-01, see `study/dates`), so a month boundary or
 * a daylight-saving change can never break a run.
 */

import {
  dayFromParts,
  isoFromDay,
  parseIsoDay,
} from "@/lib/tool-kit/study/dates";

export const MAX_HABITS = 12;
export const MAX_NAME_LENGTH = 40;

export interface Habit {
  id: string;
  name: string;
}

export interface HabitState {
  habits: Habit[];
  /** Habit id → the ISO dates that are ticked. */
  done: Record<string, string[]>;
}

export function emptyHabitState(): HabitState {
  return { habits: [], done: {} };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Reads what the tracker saved; anything malformed becomes an empty state. */
export function parseSavedHabits(raw: string | null): HabitState {
  if (!raw) {
    return emptyHabitState();
  }
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return emptyHabitState();
  }
  if (!(isRecord(data) && Array.isArray(data.habits))) {
    return emptyHabitState();
  }
  const habits: Habit[] = [];
  for (const item of data.habits.slice(0, MAX_HABITS)) {
    if (
      isRecord(item) &&
      typeof item.id === "string" &&
      typeof item.name === "string" &&
      item.id.length > 0 &&
      !habits.some((habit) => habit.id === item.id)
    ) {
      habits.push({
        id: item.id,
        name: item.name.slice(0, MAX_NAME_LENGTH),
      });
    }
  }
  const done: Record<string, string[]> = {};
  const savedDone = isRecord(data.done) ? data.done : {};
  for (const habit of habits) {
    const list = savedDone[habit.id];
    if (Array.isArray(list)) {
      done[habit.id] = list.filter(
        (day): day is string =>
          typeof day === "string" && parseIsoDay(day) !== null
      );
    }
  }
  return { habits, done };
}

/** The ticked days of one habit as a set of day numbers. */
export function tickedDays(
  isoDays: readonly string[] | undefined
): Set<number> {
  const days = new Set<number>();
  for (const iso of isoDays ?? []) {
    const day = parseIsoDay(iso);
    if (day !== null) {
      days.add(day);
    }
  }
  return days;
}

/** The list with `iso` added, or removed when it was already there. */
export function toggleIso(
  list: readonly string[] | undefined,
  iso: string
): string[] {
  const current = list ?? [];
  if (current.includes(iso)) {
    return current.filter((day) => day !== iso);
  }
  return [...current, iso].sort();
}

/**
 * The run of consecutive ticked days that ends today.
 *
 * A habit not yet ticked today keeps its streak alive: the run is counted back from
 * yesterday instead, because the day is not over. Two days without a tick ends it.
 */
export function currentStreak(
  days: ReadonlySet<number>,
  today: number
): number {
  let cursor = days.has(today) ? today : today - 1;
  let count = 0;
  while (days.has(cursor)) {
    count += 1;
    cursor -= 1;
  }
  return count;
}

/** The longest run of consecutive ticked days, anywhere in the history. */
export function longestStreak(days: ReadonlySet<number>): number {
  const sorted = [...days].sort((a, b) => a - b);
  let best = 0;
  let run = 0;
  let previous = Number.NEGATIVE_INFINITY;
  for (const day of sorted) {
    run = day === previous + 1 ? run + 1 : 1;
    best = Math.max(best, run);
    previous = day;
  }
  return best;
}

export interface MonthSummary {
  /** Ticked days in the month. */
  done: number;
  /** Days of the month that have started (all of them for a past month). */
  elapsed: number;
  /** done / elapsed as a whole percentage, or null before the month has begun. */
  percent: number | null;
}

/** Ticks inside one month, against the days that month has had so far. */
export function monthSummary(
  days: ReadonlySet<number>,
  year: number,
  month: number,
  length: number,
  today: number | null
): MonthSummary {
  const first = dayFromParts(year, month + 1, 1);
  if (first === null) {
    return { done: 0, elapsed: 0, percent: null };
  }
  let done = 0;
  for (let offset = 0; offset < length; offset += 1) {
    if (days.has(first + offset)) {
      done += 1;
    }
  }
  let elapsed = length;
  if (today !== null) {
    elapsed = Math.max(0, Math.min(length, today - first + 1));
  }
  return {
    done,
    elapsed,
    percent: elapsed > 0 ? Math.round((done / elapsed) * 100) : null,
  };
}

/** Plain-text export: one line per habit with the month's ticks and streaks. */
export function habitsText(
  rows: readonly {
    name: string;
    done: number;
    elapsed: number;
    current: number;
    longest: number;
  }[],
  labels: {
    title: string;
    streak: string;
    longest: string;
    done: string;
  }
): string {
  const lines = [labels.title, ""];
  for (const row of rows) {
    lines.push(
      `${row.name}: ${labels.done} ${row.done}/${row.elapsed}; ${labels.streak} ${row.current}; ${labels.longest} ${row.longest}`
    );
  }
  return lines.join("\n");
}

/** ISO date of day `n` of a month (1-based). */
export function isoOfMonthDay(
  year: number,
  month: number,
  dayOfMonth: number
): string | null {
  const day = dayFromParts(year, month + 1, dayOfMonth);
  return day === null ? null : isoFromDay(day);
}
